import { AfterViewInit, ChangeDetectionStrategy, Component, effect, ElementRef, inject, input, OnDestroy, output, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TextFieldModule } from '@angular/cdk/text-field';
import { L10N_LOCALE, L10nTranslatePipe } from 'angular-l10n';
import { AvailableTemplatesService } from '../available-templates.service';
import { AlertSettingsVariable } from 'src/app/hickory-api';
import { PlaceholderUtilities } from '../placeholder-utilities';
import { AlertBuilderService } from 'src/app/alert/shared/alert-builder';
import { Subscription } from 'rxjs';

export interface TemplateVariable {
  id: string;
  name: string;
  description: string;
  sample?: string;
}

export class AlwaysErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl): boolean {
    return control?.invalid;
  }
}

const trigger = '{{';

@Component({
  selector: 'app-templated-simple-input',
  templateUrl: './templated-simple-input.component.html',
  styleUrl: './templated-simple-input.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    TextFieldModule,
    L10nTranslatePipe,
  ],
})
export class TemplatedSimpleInputComponent implements AfterViewInit, OnDestroy {
  readonly locale = inject(L10N_LOCALE);
  private readonly variablesService = inject(AvailableTemplatesService);
  private readonly alertBuilder = inject(AlertBuilderService);
  private readonly disposables = new Subscription();

  inputControl = new FormControl('');

  allVariables: AlertSettingsVariable[] = [];
  filteredVariables: AlertSettingsVariable[] = [];

  displayChips = true;
  useSample = false;
  previewInput = false;

  get showPreview() {
    return this.inputHasVariables && this.inputControl?.valid && this.previewInput;
  }

  // Signal inputs
  readonly label = input('Subject');
  readonly inputReadonly = input(false);
  readonly readonlyText = input('Readonly');
  readonly showReset = input(false);
  readonly showHint = input(false);
  readonly hintClass = input('');
  readonly hintText = input('');
  readonly useIntegratedPreview = input(true);
  readonly getAllTemplates = input(false);

  // use 'input' prefix to avoid conflicts and maintain clarity
  readonly inputValue = input('');
  readonly inputDisabled = input(false);
  readonly inputRequired = input(false);

  readonly resetClicked = output<void>();
  readonly valueChange = output<string>();
  readonly modelChange = output<string>();

  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
  @ViewChild('inputEl') inputEl!: ElementRef<HTMLInputElement>;

  private currentTriggerPosition = -1;
  private originalInputValue = '';
  private originalCursorPosition = 0;
  private isUserInteraction = false;

  private readonly onInputHandler = () => this.isUserInteraction = true;

  errorMatcher = new AlwaysErrorStateMatcher();

  constructor() {
    // Effect to handle inputValue changes
    effect(() => {
      const value = this.inputValue();
      this.inputControl?.setValue(value);
      this.inputControl?.updateValueAndValidity();
    });

    // Effect to handle inputDisabled changes
    effect(() => {
      const disabled = this.inputDisabled();
      if (disabled) {
        this.inputControl?.disable();
      } else {
        this.inputControl?.enable();
      }
    });

    // Effect to handle inputRequired changes
    effect(() => {
      const required = this.inputRequired();
      if (required) {
        this.inputControl?.addValidators(Validators.required);
      } else {
        this.inputControl?.removeValidators(Validators.required);
      }
      this.inputControl?.updateValueAndValidity();
    });
  }

  get inputHasVariables() {
    if (typeof this.inputControl.value === 'string') {
      return PlaceholderUtilities.extractPlaceholders(this.inputControl.value).length != 0;
    }
    return false;
  }

  get previewHtml(): string {
    if (typeof this.inputControl.value === 'string') {
      return PlaceholderUtilities.replacePlaceholders(this.inputControl.value, (placeholderName) => {
        const foundVar = this.allVariables.find((item) => placeholderName === item.placeHolder);

        if (!foundVar) {
          return placeholderName;
        }

        return `<span class="${this.displayChips ? 'chip-style' : ''}">${this.useSample ? foundVar.sampleValue : foundVar.friendlyName}</span>`;
      });
    }

    return '';
  }

  ngAfterViewInit() {
    const templateId = this.alertBuilder?.alertTemplateId();
    const getVariables$ = this.getAllTemplates()
      ? this.variablesService.getAllAvailableTemplates(templateId)
      : this.variablesService.getAllAvailableAlertTemplates(templateId);

    this.disposables.add(
      getVariables$.subscribe((result) => {
        this.allVariables = result.items;
        this.inputControl.addValidators([this.unclosedVariableValidator(), this.variableExistsValidator()]);
        this.inputControl.updateValueAndValidity();
      }),
    );

    this.inputEl.nativeElement.addEventListener('input', this.onInputHandler);

    this.disposables.add(
      this.inputControl.valueChanges.subscribe((value) => {
        // Ensure we're always working with a string
        const stringValue = typeof value === 'string' ? value : '';
        const pos = this.inputEl.nativeElement.selectionStart;

        const context = this.getTriggerContext(stringValue, pos);

        if (context && this.isUserInteraction) {
          this.currentTriggerPosition = context.start;
          this.originalInputValue = stringValue;
          this.originalCursorPosition = pos;
          this.filteredVariables = this.filterVariables(context.query);
          this.autocompleteTrigger.openPanel();
        } else {
          this.filteredVariables = [];
          this.autocompleteTrigger.closePanel();
        }

        if (typeof value === 'string') {
          this.valueChange.emit(value);
          if (this.inputControl.dirty) {
            this.modelChange.emit(value);
          }
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.inputEl.nativeElement.removeEventListener('input', this.onInputHandler);
    this.disposables.unsubscribe();
  }

  unclosedVariableValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null; // Ignore empty values
      if (typeof control.value !== 'string') return null;

      const text: string = control.value;
      const openCount = (text.match(/{{/g) || []).length;
      const closeCount = (text.match(/}}/g) || []).length;

      if (openCount > closeCount) {
        return {
          unclosedBraces: {
            valid: false,
            message: 'Unclosed double curly braces detected.',
          },
        };
      }

      return null; // No errors
    };
  }

  onResetClick() {
    this.inputControl.markAsPristine();
    this.resetClicked.emit();
  }

  variableExistsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null; // Ignore empty values
      if (typeof control.value !== 'string') return null;

      const text: string = control.value;
      const templateValues = PlaceholderUtilities.extractPlaceholders(text);

      for (const value of templateValues) {
        const found = this.allVariables.some((variable) => variable.placeHolder === value);

        if (!found) {
          return {
            variableExists: {
              valid: false,
              message: `"${value}" is not a valid placeholder.`,
            },
          };
        }
      }

      return null; // Everything is valid
    };
  }

  private getTriggerContext(text: string, cursorPos: number | null): { start: number; query: string } | null {
    if (!text || cursorPos === null) return null;

    // Explicit type check
    if (typeof text !== 'string') return null;

    // Find the last unclosed trigger before cursor
    const textBeforeCursor = text.slice(0, cursorPos);
    const lastTriggerIndex = textBeforeCursor.lastIndexOf(trigger);

    if (lastTriggerIndex === -1) return null;

    // Check if trigger is closed
    const textAfterTrigger = text.slice(lastTriggerIndex);
    if (textAfterTrigger.includes('}}')) return null;

    const queryStart = lastTriggerIndex + trigger.length;
    const query = text.slice(queryStart, cursorPos).trim();

    return { start: lastTriggerIndex, query };
  }

  private filterVariables(query: string): AlertSettingsVariable[] {
    return this.allVariables.filter(
      (v) => v.friendlyName.toLowerCase().includes(query.toLowerCase())
    );
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    // Get the selected variable object from the option's value
    const variable = event.option.value as AlertSettingsVariable;

    // Use the original input state before autocomplete triggered
    const newValue =
      this.originalInputValue.slice(0, this.currentTriggerPosition) +
      `{{ ${variable.placeHolder} }}` +
      this.originalInputValue.slice(this.originalCursorPosition);

    // Update the form control value
    this.inputControl.setValue(newValue);

    // Set cursor position after inserted variable
    const newCursorPos = this.currentTriggerPosition + `{{ ${variable.placeHolder} }}`.length;
    setTimeout(() => {
      this.inputEl.nativeElement.setSelectionRange(newCursorPos, newCursorPos);
    });
  }
}
